import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { XStack } from 'tamagui';
import { GBAlert, GBButton, GBPageHeader, GBText } from '@greenbackapp/ui';
import { useBusiness } from '../../contexts/BusinessContext';
import { BusinessCreateFields } from './common/BusinessCreateFields';
import { suggestedTimeZone } from './common/timeZones';
import { ExperienceQuestion } from './common/ExperienceQuestion';
import { BusinessApiError } from '../../services/businessApiCommon';
import { groupAccountKey } from '../../services/billingAccountsService';
import { manageSubscriptionPath } from '../../pages/manageSubscriptionModel';
import { BusinessEntityType, CoaTemplate, UUID } from '../../types/businessApi';
import { PageFrame } from '../common/PageFrame';

export interface BusinessOnboardingProps {
  /**
   * The group they paid for before any business existed (PRD 86 §3,
   * BusinessContext's `paidEmptyGroupId`). The business is created into it,
   * and the base price already buys it, so no amount is acknowledged.
   */
  entityGroupId?: UUID;
  /** Called once step 2 is answered or skipped, for a page that shows this
   *  inline rather than letting the shell take over. */
  onDone?: () => void;
}

/** What a refused create says, and the way on if there is one. */
interface OnboardingError {
  title: string;
  message: string;
  action?: { label: string; onPress: () => void };
}

/**
 * First-run for live mode: shown inside /business when the user has no
 * businesses yet. Two steps (PRD 51 §4).
 *
 *   1. Create the business, seeding its Chart of Accounts from the template.
 *   2. Ask what the business does, which decides what the app shows them.
 *
 * ONLY THE SECOND STEP IS A QUESTION, and only because its answer changes the
 * product rather than adding a task. "Connect a bank", "bring your old books
 * across" and "invite your bookkeeper" are all work to be DONE, and work
 * belongs on a list you can come back to — the setup guide on the dashboard —
 * not in a gate between somebody and their books.
 *
 * STEP 2 CANNOT LOSE ANYTHING. The business exists the moment step 1 succeeds,
 * so skipping, closing or failing step 2 lands them on their dashboard with the
 * question waiting as the guide's first row. It is asked through the same
 * component in both places, so there is no state to reconcile between "asked
 * during onboarding" and "asked later".
 *
 * Adding a SECOND business goes through AddBusinessModal in settings, which
 * shares the field set via BusinessCreateFields.
 *
 * PRD 86 adds two props to this PRD 51 component. Someone who paid for
 * Greenback Business before naming a business passes `entityGroupId`, and the
 * business is created into the group they paid for. A page that shows this
 * inline passes `onDone`, to hear when step 2 is over.
 */
export const BusinessOnboarding: React.FC<BusinessOnboardingProps> = ({
  entityGroupId,
  onDone,
}) => {
  const { createBusiness, refresh } = useBusiness();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [entityType, setEntityType] = useState<BusinessEntityType | ''>('');
  const [template, setTemplate] = useState<CoaTemplate>('general_business');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<OnboardingError | null>(null);
  // Set once the business exists. Its presence IS the step-2 state — there is
  // no separate step counter that could disagree with whether we have a
  // business to ask about.
  const [createdBusinessId, setCreatedBusinessId] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && !submitting;

  const handleCreate = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const business = await createBusiness({
        name: name.trim(),
        entity_type: entityType || undefined,
        coa_template: template,
        // The creator is the owner, so their zone is the likeliest one. A
        // non-US or unknown zone is left unset for the profile to ask about.
        time_zone: suggestedTimeZone(),
        // The group they paid for. Never `monthly_increase_cents`: that
        // acknowledges a charge on a group that already has a business, and
        // naming the first one changes nobody's bill.
        ...(entityGroupId ? { entity_group_id: entityGroupId } : {}),
      });
      setCreatedBusinessId(business.id);
    } catch (e) {
      setError(createError(e));
      setSubmitting(false);
    }
  };

  const createError = (e: unknown): OnboardingError => {
    if (!(e instanceof BusinessApiError)) {
      return {
        title: 'Something went wrong',
        message: 'Could not create the business. Please try again.',
      };
    }
    // The group already has its business: named in another tab, or by an
    // earlier press whose answer never arrived. Only a create into the paid
    // group sends a group from here, and the api says so two ways. A
    // group holding a business is no longer "unfilled", so it is read as a
    // priced addition, and asking for `monthly_increase_cents` (which the
    // paid group never sends) is the usual answer; `group_filled` is only
    // two creates racing past that check. Either way the business exists,
    // so the way on is to it. Our words, not the api's: one asks for a
    // parameter by name and speaks of a bill that has not changed.
    // Refreshed as well as navigated: inside /business the shell keeps its
    // list across the move.
    if (e.code === 'group_filled' || e.code === 'acknowledgement_required') {
      return {
        title: 'Your business is already set up',
        message:
          'The Business plan you paid for already has its business, perhaps named in another tab. Open your businesses to carry on.',
        action: {
          label: 'Open your businesses',
          onPress: () => {
            void refresh();
            navigate('/business');
          },
        },
      };
    }
    // The group's plan has ended since the list was read: it was offered
    // only because it was paid for then (`meta.paid_empty_group_id` uses the
    // same test the create re-checks), so this is an expiry, a failed or
    // refunded payment, or a comp past its date — not a payment on its way,
    // and trying again will not help. Manage subscription shows which. The
    // refresh lets /business re-read the list, which no longer offers the
    // group, if they come back. Only for the paid group: without one this
    // is the creation gate's refusal, and its own words are the true ones.
    if (entityGroupId && e.code === 'subscription_required') {
      return {
        title: "Your Business plan isn't active",
        message:
          "The Greenback Business plan you paid for isn't active any more, so the business can't be set up in it. Its status is on Manage subscription.",
        action: {
          label: 'Manage subscription',
          onPress: () => {
            void refresh();
            navigate(manageSubscriptionPath(groupAccountKey(entityGroupId)));
          },
        },
      };
    }
    return { title: 'Something went wrong', message: e.message };
  };

  // Nothing to navigate to: BusinessShell re-renders into the dashboard once
  // the business list is non-empty, and it has been non-empty since step 1.
  // Clearing this is what hands the screen over — or, for a page that shows
  // this inline, `onDone`.
  const finishQuestion = useCallback(() => {
    setCreatedBusinessId(null);
    onDone?.();
  }, [onDone]);

  if (createdBusinessId) {
    return (
      <PageFrame gap={24} maxWidth={720} data-testid="onboarding-experience-step">
        <GBPageHeader
          title={`${name.trim()} is ready`}
          subtitle="One question, so Greenback can put the right things in front of you."
        />
        <ExperienceQuestion businessId={createdBusinessId} onDone={finishQuestion} />
        <GBText variant="bodySmall" color="secondary">
          Skipping is fine — your dashboard will ask again when you have a minute.
        </GBText>
      </PageFrame>
    );
  }

  return (
    <PageFrame gap={24} maxWidth={720}>
      <GBPageHeader
        title="Set up your business"
        subtitle="Create your business to start keeping real books in Greenback."
      />

      {error && (
        <GBAlert
          variant="error"
          title={error.title}
          message={error.message}
          action={error.action}
        />
      )}

      <BusinessCreateFields
        name={name}
        onNameChange={setName}
        entityType={entityType}
        onEntityTypeChange={setEntityType}
        template={template}
        onTemplateChange={setTemplate}
        disabled={submitting}
      />

      <XStack>
        <GBButton variant="primary" size="lg" disabled={!canSubmit} onPress={handleCreate}>
          {submitting ? 'Creating…' : 'Create business'}
        </GBButton>
      </XStack>
    </PageFrame>
  );
};

export default BusinessOnboarding;
